<template>
  <div
    ref="messagesContainerRef"
    class="min-h-0 flex-1 space-y-4 overflow-y-auto"
  >
    <div v-for="(message, index) in messages" class="space-y-4" :key="index">
      <chat-message :message="message" />
    </div>

    <chat-typing-indicator v-if="isAgentTyping" />
  </div>
  <chat-input class="mt-auto" @send="handleSendMessage" />
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch,
} from "vue";

import { storeToRefs } from "pinia";

import { SenderType } from "@/enums/ai";
import {
  leaveChannel,
  subscribeToChannelEvents,
} from "@/helpers/websockets/channels";
import { AI_AGENT_SESSIONS_CHANNEL } from "@/helpers/websockets/common";
import { useNotification } from "@/hooks/notifications";
import { useAiAgentsStore } from "@/stores/ai/agents";
import { useFiles } from "@/stores/ai/files";

import ChatInput from "@/components/cmdK/chat/ChatInput.vue";
import ChatMessage from "@/components/cmdK/chat/ChatMessage.vue";
import ChatTypingIndicator from "@/components/cmdK/chat/ChatTypingIndicator.vue";

import type { AiAgentSession } from "@/models/aiAgents";

const { session } = defineProps<{ session: AiAgentSession }>();

defineEmits<{ close: [] }>();

const aiFilesStore = useFiles();

const { getAgentSession, promptAgent } = useAiAgentsStore();
const { clearFiles } = aiFilesStore;
const { selectedFiles } = storeToRefs(aiFilesStore);
const { showMessage } = useNotification();

const { agentSessionInfo } = storeToRefs(useAiAgentsStore());

const messagesContainerRef = ref<HTMLElement | null>(null);

const messages = computed(
  () => agentSessionInfo.value.activeSession?.messages ?? []
);

const isAgentTyping = computed(() => {
  const lastMessage = messages.value.at(-1);
  return lastMessage?.sender_type === SenderType.User;
});

const scrollToBottom = () => {
  if (!messagesContainerRef.value) {
    return;
  }

  messagesContainerRef.value.scrollTop =
    messagesContainerRef.value.scrollHeight;
};

const handleSendMessage = async (message: string) => {
  try {
    await promptAgent({
      prompt: message,
      files: selectedFiles.value,
      sessionId: session.id,
    });
    await getAgentSession(session.id?.toString());
    clearFiles();
  } catch (error) {
    showMessage((error as Error).message, "error");
  }
};

// Auto-scroll to bottom when messages change
watch(
  () => [agentSessionInfo.value.activeSession?.messages?.length, isAgentTyping],
  async () => {
    await nextTick();
    scrollToBottom();
  },
  { immediate: true }
);

onBeforeMount(() => {
  subscribeToChannelEvents(AI_AGENT_SESSIONS_CHANNEL, session.id?.toString());
});

onBeforeUnmount(() => {
  leaveChannel(AI_AGENT_SESSIONS_CHANNEL, session.id?.toString());
});
</script>
